import { computed } from 'vue'
// types
import type { AppTextMarkdownHookOptions, AppTextMarkdownHookReturn } from '../types/interfaces'
import type { Token, Tokens } from 'marked'
// helpers
import { Marked } from 'marked'
import { slugify } from '@helpers/slugify'

interface RendererThis {
  parser: { parseInline: (tokens: Token[]) => string }
}

// Module-cached configured Marked instance. publicUrl is fixed per deploy
// (runtime config) so we lazy-build on first call and reuse.
let cached: Marked | null = null

const getMd = (publicUrl: string): Marked => {
  if (cached) return cached
  cached = new Marked({
    async: false,
    breaks: true,
    gfm: true,
  })
  cached.use({
    renderer: {
      heading (this: RendererThis, { depth, text, tokens }: Tokens.Heading): string {
        const inline = this.parser.parseInline(tokens)
        if (depth === 2) {
          return `<h2 id="${slugify(text)}">${inline}</h2>\n`
        }
        return `<h${depth}>${inline}</h${depth}>\n`
      },
      image ({ href, title, text }: Tokens.Image): string {
        if (!href) return ''
        const resolved = resolveMediaUrl(href, publicUrl)
        const titleAttr = title ? ` title="${title}"` : ''
        return `<img src="${resolved}" alt="${text}"${titleAttr} loading="lazy" decoding="async">`
      },
    },
  })
  return cached
}

// CKEditor fields store HTML; legacy markdown strings survive until edited
// (and on prod until its one-off conversion runs) — support both.
export const isHtmlContent = (raw: string): boolean => raw.trimStart().startsWith('<')

const stripTags = (value: string): string => value.replace(/<[^>]+>/g, '').trim()

const resolveMediaUrl = (url: string, publicUrl: string): string => {
  const isAbsolute = /^https?:\/\//.test(url) || url.startsWith('//')
  if (isAbsolute) return url
  const base = publicUrl.replace(/\/+$/, '')
  const path = url.startsWith('/') ? '' : '/'
  return `${base}${path}${url}`
}

const addHeadingIds = (html: string): string => (
  html.replace(/<h2(?![^>]*\bid=)([^>]*)>([\s\S]*?)<\/h2>/gi, (_, attrs: string, inner: string) => (
    `<h2 id="${slugify(stripTags(inner))}"${attrs}>${inner}</h2>`
  ))
)

const processImages = (html: string, publicUrl: string): string => (
  html.replace(/<img\b[^>]*>/gi, (tag) => {
    let out = tag
      .replace(/\bsrc="([^"]+)"/i, (_, src: string) => `src="${resolveMediaUrl(src, publicUrl)}"`)
      .replace(/\bsrcset="([^"]+)"/i, (_, srcset: string) => {
        const resolved = srcset.split(',').map((entry) => {
          const [url, size] = entry.trim().split(/\s+/)
          return [resolveMediaUrl(url ?? '', publicUrl), size].filter(Boolean).join(' ')
        }).join(', ')
        return `srcset="${resolved}"`
      })
    if (!/\bloading=/.test(out)) out = out.replace(/^<img/i, '<img loading="lazy"')
    if (!/\bdecoding=/.test(out)) out = out.replace(/^<img/i, '<img decoding="async"')
    return out
  })
)

export const processRichHtml = (html: string, publicUrl: string): string => (
  processImages(addHeadingIds(html), publicUrl)
)

export const useAppTextMarkdown = ({ props }: AppTextMarkdownHookOptions): AppTextMarkdownHookReturn => {
  const { public: { strapi } } = useRuntimeConfig()

  const refHtml = computed<string>(() => {
    const raw = props.content as unknown
    if (!raw || typeof raw !== 'string') return ''
    if (isHtmlContent(raw)) {
      return processRichHtml(raw, strapi.publicUrl)
    }
    const md = getMd(strapi.publicUrl)
    const result = md.parse(raw)
    return typeof result === 'string' ? result : ''
  })

  return {
    refHtml,
  }
}
